import { Apollo, gql } from 'apollo-angular';

import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { ProductDefinition, CartLineItem, Mutation, CoreLibService, ProductType, LinkOpenMode } from 'core-lib';
import { PaymentLibService } from '../payment-lib.service';
import { PaymentIntent } from '../payment.models';
import { MessageService } from 'primeng/api';
import { interval, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';


@Component({
    selector: 'lib-payment-shopping-cart',
    templateUrl: './payment-shopping-cart.component.html',
    styleUrls: ['./payment-shopping-cart.component.scss'],
    standalone: false
})
export class PaymentShoppingCartComponent implements OnInit {

  @Input() intent: PaymentIntent;
  @Output() pay = new EventEmitter<any>();
  @Output() goto = new EventEmitter<string>();

  products: ProductDefinition[] = []
  shoppingCartItems: CartLineItem[] = [];
  cartPrice: { amount: number, currency: string, tax: number } = { amount: 0, currency: 'eur', tax: 0 };
  waitingForPayment = false;
  itemsInWallet = 0;
  checkIncomingPaymentsTimer: any;
  lastCheck: Date;

  maxQuantity: { [key: string]: number } = {};

  CREATE_PAYMENT_SESSION = gql`
  mutation createPaymentSession($items : [CartLineItem], $redirectUrl: String!, $context: JSON){
    createPaymentSession(items : $items,redirectUrl: $redirectUrl, context: $context){
      id
      url
    }
  }`;


  constructor(private apollo: Apollo, private paymentService: PaymentLibService, private coreLibService: CoreLibService, private messageService: MessageService) {

  }

  ngOnInit(): void {
    this.paymentService.listProductsByIds(this.intent.productDefinitionIds).subscribe((result) => {
      this.products = result.filter(p=>p.active == true);
      this.shoppingCartItems = this.products.map((product, index) => {
        let initQuantity = 0;
        this.maxQuantity[product.id] = 100;
        if (product.type === ProductType.SingleTicket && index == 0) {
          initQuantity = this.intent.positions.length;
          if (product.preventStockpile) {
            this.maxQuantity[product.id] = initQuantity;
          }
        } else {
          if (product.preventStockpile) {
            this.maxQuantity[product.id] = 1;
          }
        }
        const position: CartLineItem = { productDefinitionId: product.id, quantity: initQuantity };
        return position;
      });

      this.updateCartSum();
    });
    this.paymentService.loadWallet().subscribe((items) => {
      this.itemsInWallet = items.length;
    });
  }

  /**
   * user changed the quantity
   * @param index 
   */
  onCartQuantityChanged(index: number): void {
    const cartItem = this.shoppingCartItems[index];
    if (cartItem.quantity < 0) {
      this.shoppingCartItems[index].quantity = 0;
    }

    const maxQuantity = this.maxQuantity[cartItem.productDefinitionId];
    if (maxQuantity) {
      if (cartItem.quantity > maxQuantity) {
        this.shoppingCartItems[index].quantity = maxQuantity;
      }
    }

    this.updateCartSum();
  }

  /**
   * updats shopping card sum
   */
  updateCartSum(): void {
    this.cartPrice.amount = 0;
    this.cartPrice.tax = 0;
    for (let i = 0; i < this.shoppingCartItems.length; i++) {
      const product = this.products[i];
      const item = this.shoppingCartItems[i];
      if (item.quantity == 0) {
        continue;
      }
      const lineAmount = item.quantity * product.price.amount;
      this.cartPrice.amount += lineAmount;
      this.cartPrice.currency = product.price.currency;

      const lineNetto = lineAmount / (product.tax.rate / 100 + 1)
      const lineTax = lineAmount - lineNetto;
      this.cartPrice.tax += lineTax;
    }
  }
  /**
   * 
   * @param index 
   * @param delta 
   */
  onChangeCartQuantity(index: number, delta: number) {
    this.shoppingCartItems[index].quantity += delta;
    this.onCartQuantityChanged(index);
  }


  /**
   * 
   */
  onPay(): void {
    this.apollo.mutate<Mutation>({
      mutation: this.CREATE_PAYMENT_SESSION,
      variables: {
        items: this.shoppingCartItems.filter(ci => ci.quantity > 0),
        redirectUrl: this.intent.successUrl,
        context: {}
      }
    }).subscribe((result) => {
      if (!result.errors) {
        this.waitingForPayment = true;
        this.checkForIncomingPayment();
        this.coreLibService.openLink(result.data.createPaymentSession.url, LinkOpenMode.External,null);
      }
    });
  }

  /**
   * wait for incoming payment
   */
  checkForIncomingPayment(): void {
    const stopPolling = new Subject<void>();
    interval(1000).pipe(
      switchMap(() => this.paymentService.loadWallet()), takeUntil(stopPolling)).subscribe((items) => {
        this.lastCheck = new Date();
        if (this.itemsInWallet != items.length) {
          stopPolling.next();
          stopPolling.complete();
          this.messageService.add({ severity: 'success', summary: $localize`:@@payment-shopping-cart.payment-confirmed:Payment confirmed. You can now complete your booking.` });
          this.goto.emit('booking');
        }
      });
  }
}

